/**
 * Whole-screen error state: the page could not be shown at all, as opposed to
 * `ErrorBanner`, which sits above a page that still works.
 *
 * Always offers a way out. `Try again` appears only when the caller has
 * something to retry, but the back link is unconditional — the 404 route and a
 * failed video load both reach this, and a dead end with no navigation is how a
 * mistyped URL turns into a closed tab.
 */
import type { ReactNode } from 'react';
import { Button, ButtonLink } from '@/shared/ui/Button';

export interface ErrorPageProps {
  title?: string;
  message: string;
  backLabel?: string;
  backTo?: string;
  onRetry?: () => void;
  children?: ReactNode;
}

export function ErrorPage({
  title = 'Something went wrong',
  message,
  backLabel = 'Back to Library',
  backTo = '/',
  onRetry,
  children,
}: ErrorPageProps) {
  return (
    <section className="error-page" role="alert" aria-live="assertive">
      <div className="error-page__icon" aria-hidden="true">⚠</div>
      <h2 className="error-page__title">{title}</h2>
      <p className="error-page__message">{message}</p>
      {children}
      <div className="error-page__actions">
        {onRetry && (
          <Button variant="primary" onClick={onRetry}>
            Try again
          </Button>
        )}
        {/* A real link, not a button that navigates: this is the escape hatch
            from a dead end, and it should be middle-clickable like any other. */}
        <ButtonLink variant="secondary" to={backTo}>
          {backLabel}
        </ButtonLink>
      </div>
    </section>
  );
}
